export interface BillingLimits {
  eventsAmount: number;
  domainsAmount: number;
  campaignsAmount: number;
  offersAmount: number;
  landingsAmount: number;
  flowsAmount: number;
  trafficSourcesAmount: number;
  affiliateNetworksAmount: number;
  customColumnsAmount: number;
  memberAdminsAmount: number;
  memberUsersAmount: number;
  dataRetention: number;
  presetAmount: number;
  bulkEditCampaigns: boolean;
  bulkEditOffers: boolean;
  bulkEditLandings: boolean;
  conversionsMaxTry: boolean;
  webViewCondition: boolean;
  uniqOfferViewerCondition: boolean;
  crawlerCondition: boolean;
  proxyCondition: boolean;
  referrerDomainCondition: boolean;
  visitCapCondition: boolean;
  botMarkCondition: boolean;
  uniquenessPeriodCondition: boolean;
  doubleMetaRedirect: boolean;
  uniquenessPeriod: boolean;
  bulkReportCampaign: boolean;
  bulkReportCustomParams: boolean;
  bulkReportOffer: boolean;
  bulkReportLanding: boolean;
  bulkReportFlow: boolean;
  bulkReportTrafficSource: boolean;
  bulkReportAffiliateNetwork: boolean;
  importOffers: boolean;
  importLandings: boolean;
  markersCampaigns: boolean;
  markersOffers: boolean;
  markersLandings: boolean;
  markersFlows: boolean;
  markersAffiliateNetworks: boolean;
  markersTrafficSources: boolean;
  markersCustomParams: boolean;
  trafficSourceParamMasking: boolean;
  botFiltersAccess: boolean;
  redirectDomainsAmount: boolean;
  customConversionsAmount: boolean;
  sharedReportsAmount: boolean;
  sendPostbacksCampaign: boolean;
  trafficLossCampaign: boolean;
  landingPageProtection: boolean;
  tagsCampaigns: boolean;
  tagsOffers: boolean;
  tagsLandings: boolean;
  tagsFlows: boolean;
  tagsTrafficSources: boolean;
  tagsAffiliateNetworks: boolean;
  integrationMgid: boolean;
  integrationExoclick: boolean;
  integrationTrafficjunky: boolean;
  integrationPropellerads: boolean;
  integrationFacebookConversions: boolean;
  integrationRevcontent: boolean;
  smartRotations: boolean;
  ivtIpIsDatacenter: boolean;
  ivtIpIsBlacklisted: boolean;
  ivtFastClicker: boolean;
  ivtIpWithFrequentCampaignEvents: boolean;
  ivtIpWithFrequentEvents: boolean;
  externalIdCondition: boolean;
  compareReport: boolean;
}

export interface PlanAddon {
  addonLimit: number;
  addonName: keyof BillingLimits;
  addonTotalPrice: number;
  addonUnitPrice: number;
  addonUsage: number;
  pending?: 'delete' | 'create' | 'update';
}

export interface BillingPlan {
  dataRetention: number;
  domainsIncluded: number;
  eventsIncluded: number;
  id: string;
  name: string;
  overageCost: number;
  period: number;
  periodUnit: 'month';
  price: number;
  priceOld: number;
  limits: BillingLimits;
  addons: PlanAddon;
  nextPlan: {
    planName: string;
  };
  usage: {
    eventsAmount: number;
    domainsAmount: number;
    campaignsAmount: number;
    offersAmount: number;
    landingsAmount: number;
    flowsAmount: number;
    trafficSourcesAmount: number;
    affiliateNetworksAmount: number;
    customColumnsAmount: number;
    memberAdminsAmount: boolean;
    memberUsersAmount: boolean;
  };
}

export interface BillingSubscription {
  planId: string;
  eventsUsed: number;
  eventsIncluded: number;
  subscriptionEnd: number;
  limits: BillingLimits;
}

export interface BillingSubscriptionState extends BillingSubscription {
  planName?: string;
  planPrice?: number;
  planMrr?: number;
  nextPlan?: { planName?: string };
  subscriptionStart?: number;
  eventsOverage?: number;
  overageWorth?: number;
  overageCost?: number;
  usage?: Partial<Record<keyof BillingLimits, number>> & Record<string, number>;
  addons?: Partial<Record<string, PlanAddon>>;
  pending?: boolean;
  promoCodesUsed?: Array<{ appliedCount?: number }>;
}

export interface BillingCustomerState {
  balance?: number;
  billingInfo?: Record<string, unknown>;
  paymentMethod?: {
    type?: string;
    card?: {
      brand?: string;
      last4?: string | number;
      expiryMonth?: number;
      expiryYear?: number;
    };
  } | null;
}

export const availablePlanLimits: (keyof BillingLimits)[] = [
  'dataRetention',
  'eventsAmount',
  'domainsAmount',
  'campaignsAmount',
  'offersAmount',
  'landingsAmount',
  'flowsAmount',
  'trafficSourcesAmount',
  'affiliateNetworksAmount',
  'customColumnsAmount',
  'memberAdminsAmount',
  'memberUsersAmount',
  'bulkEditCampaigns',
  'bulkEditOffers',
  'bulkEditLandings',
  'integrationMgid',
  'integrationExoclick',
  'integrationPropellerads',
  'integrationFacebookConversions',
  'integrationRevcontent',
  'integrationTrafficjunky',
  'webViewCondition',
  'crawlerCondition',
  'proxyCondition',
  'referrerDomainCondition',
  'visitCapCondition',
  'botMarkCondition',
  'uniquenessPeriodCondition',
  'doubleMetaRedirect',
  'uniquenessPeriod',
  'bulkReportCampaign',
  'bulkReportOffer',
  'bulkReportLanding',
  'bulkReportFlow',
  'bulkReportTrafficSource',
  'bulkReportAffiliateNetwork',
  'importOffers',
  'importLandings',
  'markersCampaigns',
  'markersOffers',
  'markersLandings',
  'markersFlows',
  'markersAffiliateNetworks',
  'markersTrafficSources',
  'markersCustomParams',
  'trafficSourceParamMasking',
  'botFiltersAccess',
  'redirectDomainsAmount',
  'customConversionsAmount',
  'sharedReportsAmount',
  'sendPostbacksCampaign',
  'trafficLossCampaign',
  'landingPageProtection',
  'tagsCampaigns',
  'tagsOffers',
  'tagsLandings',
  'tagsFlows',
  'tagsTrafficSources',
  'tagsAffiliateNetworks',
  'smartRotations',
  'bulkReportCustomParams',
  'bulkReportBrowserNames',
  'bulkReportBrowserVersions',
  'bulkReportCities',
  'bulkReportConnectionTypes',
  'bulkReportCountries',
  'bulkReportDay',
  'bulkReportDayOfWeek',
  'bulkReportDeviceModels',
  'bulkReportDeviceTypes',
  'bulkReportDeviceVendors',
  'bulkReportHourOfDay',
  'bulkReportIp',
  'bulkReportIsp',
  'bulkReportLanguages',
  'bulkReportMobileCarriers',
  'bulkReportMonth',
  'bulkReportOs',
  'bulkReportOsVersions',
  'bulkReportReferrerDomains',
  'bulkReportReferrers',
  'bulkReportScreenResolutions',
  'bulkReportStatesRegions',
  'bulkReportSubnetBig',
  'bulkReportSubnetSmall',
  'bulkReportUserAgents',
  'ivtIpIsDatacenter',
  'ivtIpIsBlacklisted',
  'ivtFastClicker',
  'ivtIpWithFrequentCampaignEvents',
  'ivtIpWithFrequentEvents',
  'uniqOfferViewerCondition',
  'externalIdCondition',
  'presetAmount',
  'compareReport',
];
